/* eslint-disable @typescript-eslint/naming-convention */
import React, { useLayoutEffect, useRef, useState } from 'react';

function toLocal(rect: DOMRect, container: HTMLElement) {
  const c = container.getBoundingClientRect();
  return {
    x: rect.left - c.left,
    y: rect.top - c.top,
    w: rect.width,
    h: rect.height,
    cx: rect.left - c.left + rect.width / 2,
    cy: rect.top - c.top + rect.height / 2,
    leftCenter: {
      x: rect.left - c.left,
      y: rect.top - c.top + rect.height / 2,
    },
    rightCenter: {
      x: rect.left - c.left + rect.width,
      y: rect.top - c.top + rect.height / 2,
    },
  };
}

export const AboutUsSection: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const centerRef = useRef<HTMLDivElement>(null);
  const companyRef = useRef<HTMLDivElement>(null);
  const missionRef = useRef<HTMLDivElement>(null);
  const visionRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<any>({});

  useLayoutEffect(() => {
    const cont = containerRef.current;
    if (!cont) return;

    const measure = () => {
      const getBox = (el?: HTMLElement | null) => {
        if (!el) return {};
        const r = el.getBoundingClientRect();
        return toLocal(r, cont);
      };
      const rect = cont.getBoundingClientRect();
      setCoords({
        center: getBox(centerRef.current),
        company: getBox(companyRef.current),
        mission: getBox(missionRef.current),
        vision: getBox(visionRef.current),
        w: rect.width,
        h: rect.height,
      });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(cont);
    window.addEventListener('resize', measure);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  const makeStraightLine = (from: any, to: any) => {
    if (!from || !to) return '';
    return `M ${from.cx},${from.cy} L ${to.x},${to.y}`;
  };

  const emittedDots = Array.from({ length: 10 }).map((_, i) => ({
    angle: i * 36 * (Math.PI / 180),
    distance: 150 + Math.random() * 120,
  }));

  return (
    <section
      id="about"
      className="relative py-32 overflow-hidden bg-gradient-to-b from-[#041421] via-[#03010E] to-[#020812]"
    >
      <style>{`
        @keyframes softPulse {
          0%, 100% {
            opacity: 0.5;
            filter: drop-shadow(0 0 8px rgba(255,255,255,0.5));
          }
          50% {
            opacity: 1;
            filter: drop-shadow(0 0 14px rgba(255,255,255,1));
          }
        }

        .pulse-dot {
          animation: softPulse 4s ease-in-out infinite;
        }

        .glow-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 0 35px rgba(255, 255, 255, 0.3);
          transition: all 0.4s ease;
        }
      `}</style>

      {/* ✨ Layered glow overlays for depth */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(0,212,255,0.15),transparent_60%)]" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#03010E] via-transparent to-[#041421]/30" />

      <div ref={containerRef} className="relative container mx-auto px-6 md:px-12 text-white">
        {/* --- BACKGROUND (lines + glow behind everything) --- */}
        <div className="absolute inset-0 z-0 pointer-events-none">
          <svg
            className="absolute inset-0 w-full h-full"
            viewBox={`0 0 ${coords.w || 1000} ${coords.h || 800}`}
            preserveAspectRatio="none"
          >
            <defs>
              <radialGradient id="glowGrad" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#00D4FF" stopOpacity="0.45" />
                <stop offset="100%" stopColor="#03010E" stopOpacity="0" />
              </radialGradient>
            </defs>

            {/* background hub glow */}
            {coords.center && (
              <circle
                cx={coords.center.cx}
                cy={coords.center.cy}
                r="90"
                fill="url(#glowGrad)"
                opacity="0.9"
              />
            )}

            {/* background decorative lines & dots */}
            {coords.center &&
              emittedDots.map((dot, i) => {
                const tx = Math.cos(dot.angle) * dot.distance;
                const ty = Math.sin(dot.angle) * dot.distance;
                const x2 = coords.center.cx + tx;
                const y2 = coords.center.cy + ty;
                return (
                  <g key={i}>
                    <line
                      x1={coords.center.cx}
                      y1={coords.center.cy}
                      x2={x2}
                      y2={y2}
                      stroke="rgba(255,255,255,0.15)"
                      strokeWidth="1"
                    />
                    <circle
                      cx={x2}
                      cy={y2}
                      r="3"
                      fill="white"
                      opacity="0.8"
                      className="pulse-dot"
                    />
                  </g>
                );
              })}

            {/* connecting lines to main cards */}
            <path
              d={makeStraightLine(coords.center, coords.company?.rightCenter)}
              stroke="#00D4FF"
              strokeWidth="3"
              fill="none"
            />
            <path
              d={makeStraightLine(coords.center, coords.mission?.rightCenter)}
              stroke="#00D4FF"
              strokeWidth="3"
              fill="none"
            />
            <path
              d={makeStraightLine(coords.center, coords.vision?.leftCenter)}
              stroke="#00D4FF"
              strokeWidth="3"
              fill="none"
            />

            {/* center dot */}
            {coords.center && (
              <circle
                cx={coords.center.cx}
                cy={coords.center.cy}
                r="10"
                fill="white"
                style={{
                  filter: 'drop-shadow(0 0 14px white)',
                }}
              />
            )}
          </svg>
        </div>

        {/* --- FRONT overlay dots (above boxes) --- */}
        <div className="absolute inset-0 pointer-events-none z-20">
          {[
            coords.company?.rightCenter,
            coords.mission?.rightCenter,
            coords.vision?.leftCenter,
          ].map(
            (p, i) =>
              p && (
                <div
                  key={i}
                  className="absolute w-3 h-3 rounded-full bg-white animate-pulse"
                  style={{
                    left: `${p.x - 6}px`,
                    top: `${p.y - 6}px`,
                    boxShadow: '0 0 15px rgba(255,255,255,0.9)',
                  }}
                />
              ),
          )}
        </div>

        {/* hidden hub reference */}
        <div
          ref={centerRef}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4"
        />

        {/* --- CONTENT --- */}
        <h2 className="text-3xl md:text-4xl font-extrabold text-white text-left mb-2">ABOUT US</h2>
        <p className="text-[#9EE7FF] text-lg md:text-xl mb-20 bg-clip-text text-transparent bg-gradient-to-r from-white to-[#00D4FF] opacity-80">
          We bridge innovation, precision, and expertise in occupational hygiene.
        </p>

        <div className="grid md:grid-cols-2 gap-20 relative z-10">
          {/* LEFT COLUMN */}
          <div className="flex flex-col gap-24">
            <div
              ref={companyRef}
              className="relative glow-card bg-white/5 backdrop-blur-md rounded-xl p-6"
            >
              <div className="bg-gradient-to-r from-[#00D4FF] to-[#0094FF] inline-block px-6 py-3 rounded-md font-bold text-white shadow-lg">
                OUR COMPANY
              </div>
              <p className="mt-4 text-[#C7E6F5] text-base md:text-lg leading-relaxed">
                We are experienced consultants within the Occupational Hygiene industry. Our team is
                determined to revolutionize the industry through innovative, reliable, and
                cost-effective technologies that deliver lasting results.
              </p>
            </div>

            <div
              ref={missionRef}
              className="relative glow-card bg-white/5 backdrop-blur-md rounded-xl p-6"
            >
              <div className="bg-gradient-to-r from-[#00D4FF] to-[#0094FF] inline-block px-6 py-3 rounded-md font-bold text-white shadow-lg">
                OUR MISSION
              </div>
              <p className="mt-4 text-[#C7E6F5] text-base md:text-lg leading-relaxed">
                Our mission is to deliver precise, well-structured data and professionally developed
                reports that empower businesses to make informed decisions efficiently — providing
                occupational hygiene solutions that combine accuracy, reliability, and practical
                value.
              </p>
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div className="flex flex-col justify-center relative z-10">
            <div
              ref={visionRef}
              className="relative glow-card bg-white/5 backdrop-blur-md rounded-xl p-6"
            >
              <div className="bg-gradient-to-r from-[#00D4FF] to-[#0094FF] inline-block px-6 py-3 rounded-md font-bold text-white shadow-lg">
                OUR VISION
              </div>
              <p className="mt-4 text-[#C7E6F5] text-base md:text-lg leading-relaxed">
                Turning data into impact — we boost productivity, safety, and client access with
                quality-assured insights. We aim to advance occupational hygiene consulting by
                transforming workplace health insights into measurable business value.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutUsSection;
